import { useEffect } from 'react';

/**
 * Hover and pressed are pointer states, so a page cannot draw them at rest. This
 * copies every `:hover` and `:active` rule on the page into a class twin,
 * `.is-hover` and `.is-pressed`, so a library cell can wear the theme's own
 * hover and pressed styles rather than a drawing of them.
 *
 * Focus needs none of this: MUI marks it with `.Mui-focusVisible`, a class.
 */
export const FORCED = { hover: 'is-hover', pressed: 'is-pressed' } as const;

const PSEUDOS: [RegExp, string][] = [
  [/:hover/g, `.${FORCED.hover}`],
  [/:active/g, `.${FORCED.pressed}`],
];

// Rules already copied, so a second pass adds only what is new.
const seen = new WeakSet<CSSRule>();

const twin = (rule: CSSStyleRule): string | null => {
  let selector = rule.selectorText;
  for (const [pseudo, cls] of PSEUDOS) selector = selector.replace(pseudo, cls);
  return selector === rule.selectorText
    ? null
    : `${selector} { ${rule.style.cssText} }`;
};

const collect = (rules: CSSRuleList, out: string[]) => {
  for (const rule of Array.from(rules)) {
    if (seen.has(rule)) continue;
    seen.add(rule);
    if (rule instanceof CSSStyleRule) {
      const copy = twin(rule);
      if (copy) out.push(copy);
    } else if (rule instanceof CSSMediaRule) {
      // MUI resets hover for touch screens under (hover: none). Copying that
      // would undo the hover it sits beside.
      if (rule.conditionText.includes('hover: none')) continue;
      const inner: string[] = [];
      collect(rule.cssRules, inner);
      if (inner.length)
        out.push(`@media ${rule.conditionText} { ${inner.join(' ')} }`);
    }
  }
};

let sheet: HTMLStyleElement | null = null;

const copyPseudoRules = () => {
  const out: string[] = [];
  for (const s of Array.from(document.styleSheets)) {
    if (s.ownerNode === sheet) continue;
    try {
      collect(s.cssRules, out);
    } catch {
      // A cross-origin sheet (a web font) cannot be read, and holds no
      // component styles, so there is nothing to copy from it.
    }
  }
  if (!out.length) return;
  if (!sheet) {
    sheet = document.createElement('style');
    sheet.dataset.loft = 'forced-states';
    document.head.appendChild(sheet);
  }
  sheet.append(out.join('\n'));
};

/** Copies the pointer-state rules after every render of the page using it. */
export const useForcedStates = () => {
  useEffect(copyPseudoRules);
};
